import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { createContext, useContext, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, BookOpen, ChevronDown, Gift, LogOut, Monitor, Moon, PanelLeft, Settings, Sun, User } from 'lucide-react'
import { useNavigation, useActiveRoute } from '../app/useNavigation'
import { useTranslation } from 'react-i18next'
import { useAuthSession } from '../features/auth/session'
import { useTheme } from '@/app/ThemeProvider'
import { getIacrmConfig, hasIacrmConfig, IACRM_CONFIG_EVENT } from '../features/iacrm/api'
import { fetchNotifications } from '../features/notifications/api'
import { NotificationBell } from '../features/notifications/components/NotificationBell'
import type { NotificationListEnvelope } from '@/types/notifications'
import { fetchPointsSummary } from '../features/points/api'
import { isCurrentBusinessAgent, isCurrentBusinessOwner, isSuperAdminUser } from '../lib/auth-scope'
import { formatAppNumber } from '@/lib/locale'
import { AppSidebar } from './AppSidebar'
import { OnboardingHighlight } from '@/features/onboarding/components/OnboardingHighlight'
import {
  invalidateOwnerRefreshQueries,
  isOwnerRefreshNotification,
  useOwnerRefreshPolicy,
} from '@/features/dashboard/owner-refresh'
import { LanguageSwitcher } from '@/components/app/LanguageSwitcher'
import { CookieConsentBanner } from '@/features/legal/components/CookieConsentBanner'
import { TermsAcceptanceModal } from '@/features/legal/components/TermsAcceptanceModal'
import { apiRequest } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { AgentAvatarFallback, Avatar, AvatarImage } from '@/components/ui/avatar'
import { avatarSeedForUser } from '@/lib/avatar-fallback'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'


const BreadcrumbDetailTitleContext = createContext<Dispatch<SetStateAction<string | null>> | null>(null)
type AppBreadcrumbItem = {
  label: string
  to?: string | null
}
const BreadcrumbTrailContext = createContext<Dispatch<SetStateAction<AppBreadcrumbItem[] | null>> | null>(null)

export function useAppBreadcrumbDetailTitle(title: string | null) {
  const setBreadcrumbDetailTitle = useContext(BreadcrumbDetailTitleContext)

  useEffect(() => {
    if (!setBreadcrumbDetailTitle) return

    setBreadcrumbDetailTitle(title)

    return () => {
      setBreadcrumbDetailTitle(null)
    }
  }, [setBreadcrumbDetailTitle, title])
}

export function useAppBreadcrumbTrail(trail: AppBreadcrumbItem[] | null) {
  const setBreadcrumbTrail = useContext(BreadcrumbTrailContext)

  useEffect(() => {
    if (!setBreadcrumbTrail) return

    setBreadcrumbTrail(trail)

    return () => {
      setBreadcrumbTrail(null)
    }
  }, [setBreadcrumbTrail, trail])
}

export function AppShell() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission, logout, logoutPending, user } = useAuthSession()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    // Start collapsed if in compact desktop range
    const w = window.innerWidth
    return w >= 768 && w < 1280
  })
  const [sidebarMode, setSidebarMode] = useState<'drawer' | 'collapsed-desktop' | 'normal-desktop'>(() => {
    if (typeof window === 'undefined') return 'normal-desktop'
    const w = window.innerWidth
    if (w < 768) return 'drawer'
    if (w < 1280) return 'collapsed-desktop'
    return 'normal-desktop'
  })
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const isMobile = sidebarMode === 'drawer'
  const { theme, resolvedTheme, setTheme } = useTheme()
  const [breadcrumbDetailTitle, setBreadcrumbDetailTitle] = useState<string | null>(null)
  const [breadcrumbTrail, setBreadcrumbTrail] = useState<AppBreadcrumbItem[] | null>(null)
  const [termsModalOpen, setTermsModalOpen] = useState(false)
  const [termsModalType, setTermsModalType] = useState<'platform' | 'business' | 'agent' | 'both' | 'agent-both'>('platform')
  const [termsModalBusinessId, setTermsModalBusinessId] = useState<string | undefined>(undefined)
  const [iacrmConfigured, setIacrmConfigured] = useState(() => {
    if (typeof window === 'undefined') return true
    return hasIacrmConfig()
  })
  const isSuperAdmin = isSuperAdminUser(user)
  const isBusinessOwner = isCurrentBusinessOwner(user)
  const { queryOptions: ownerNotificationRefreshOptions } = useOwnerRefreshPolicy(isBusinessOwner)

  const notificationsQuery = useQuery<NotificationListEnvelope>({
    queryKey: ['notifications', 'header'],
    queryFn: fetchNotifications,
    staleTime: 30_000,
    refetchOnWindowFocus: ownerNotificationRefreshOptions.refetchOnWindowFocus,
    refetchInterval: ownerNotificationRefreshOptions.refetchInterval,
  })


  const pointsSidebarQuery = useQuery({
    queryKey: ['points', 'sidebar-available'],
    queryFn: () => fetchPointsSummary(),
    staleTime: 30_000,
    enabled: hasPermission('points.view') && !isSuperAdmin,
  })

  const shouldShowIacrmCta = !iacrmConfigured && (isSuperAdmin || isBusinessOwner)
  // These paths are business-scoped management surfaces — not relevant for platform admin
  const superAdminHiddenPaths = ['/agents', '/prospects', '/commissions', '/payouts', '/exchange-packs', '/transactions']

  const navigation = useNavigation()
  
  const visibleNavigation = navigation.filter((route) => {
    if (route.hidden) return false
    if (!hasPermission(...route.permissions)) return false
    if (isSuperAdmin && superAdminHiddenPaths.includes(route.path)) return false
    return true
  })

  const activeRoute = useActiveRoute(location.pathname) ?? navigation[0]
  const isProgramDocsRoute = location.pathname === '/programs/docs'
  const isDashboardRoute =
    location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/')
  const dashboardSurfaceClass = 'bg-background'

  const lastOwnerRefreshNotificationRef = useRef<string | null>(null)
  const availablePointsRaw = pointsSidebarQuery.data?.data.available_points ?? 0
  const availablePoints = Math.round(availablePointsRaw)
  const availablePointsBadge = `${formatAppNumber(availablePoints)} ${t('common.pts')}`
  const userInitial = user?.display_name?.trim().charAt(0).toUpperCase() ?? t('common.userInitial')
  const displayName = user?.display_name?.trim() ?? t('common.user')
  const isAgent = isCurrentBusinessAgent(user)
  const shouldShowAgentPointsCta = isAgent && hasPermission('points.view')

  const pathSegments = location.pathname.split('/').filter(Boolean)
  const isExchangePackDetailRoute = pathSegments[0] === 'exchange-packs' && Boolean(pathSegments[1])
  const hasDeepPath = pathSegments.length > 2
  const isIdLike = (value: string) =>
    /^\d+$/.test(value) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)

  const shouldShowEllipsis =
    !isExchangePackDetailRoute && (
      hasDeepPath || pathSegments.some((segment, index) => index > 0 && isIdLike(segment))
    )
  const defaultBreadcrumbTrail: AppBreadcrumbItem[] = isProgramDocsRoute
    ? [
        { label: t('navigation.programs'), to: '/programs' },
        { label: t('programs.documentation') },
      ]
    : isExchangePackDetailRoute
      ? [
          { label: t('navigation.exchangePacks'), to: '/exchange-packs' },
          { label: breadcrumbDetailTitle ?? activeRoute.title },
        ]
      : [{ label: activeRoute.title }]
  const effectiveBreadcrumbTrail = breadcrumbTrail ?? defaultBreadcrumbTrail
  const visibleBreadcrumbTrail =
    effectiveBreadcrumbTrail.length > 2
      ? effectiveBreadcrumbTrail.slice(-2)
      : effectiveBreadcrumbTrail
  const collapsedBreadcrumbTrail =
    effectiveBreadcrumbTrail.length > 2
      ? effectiveBreadcrumbTrail.slice(0, -2)
      : []

  useEffect(() => {
    if (!isBusinessOwner) {
      lastOwnerRefreshNotificationRef.current = null
      return
    }

    const latestOwnerRefreshNotification = (notificationsQuery.data?.data ?? []).find(isOwnerRefreshNotification)
    if (!latestOwnerRefreshNotification) {
      return
    }

    if (lastOwnerRefreshNotificationRef.current === null) {
      lastOwnerRefreshNotificationRef.current = latestOwnerRefreshNotification.id
      return
    }

    if (lastOwnerRefreshNotificationRef.current === latestOwnerRefreshNotification.id) {
      return
    }

    lastOwnerRefreshNotificationRef.current = latestOwnerRefreshNotification.id
    void invalidateOwnerRefreshQueries(queryClient)
  }, [isBusinessOwner, notificationsQuery.data?.data, queryClient])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mobileQuery = window.matchMedia('(max-width: 767px)')
    const compactQuery = window.matchMedia('(min-width: 768px) and (max-width: 1279px)')

    const syncMode = () => {
      if (mobileQuery.matches) {
        setSidebarMode('drawer')
        setMobileSidebarOpen(false)
      } else if (compactQuery.matches) {
        setSidebarMode('collapsed-desktop')
        setSidebarCollapsed(true)
        setMobileSidebarOpen(false)
      } else {
        setSidebarMode('normal-desktop')
        setMobileSidebarOpen(false)
      }
    }

    syncMode()
    mobileQuery.addEventListener('change', syncMode)
    compactQuery.addEventListener('change', syncMode)

    return () => {
      mobileQuery.removeEventListener('change', syncMode)
      compactQuery.removeEventListener('change', syncMode)
    }
  }, [])

  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncConfigState = () => {
      setIacrmConfigured(hasIacrmConfig(getIacrmConfig()))
    }

    syncConfigState()
    window.addEventListener(IACRM_CONFIG_EVENT, syncConfigState)
    window.addEventListener('storage', syncConfigState)

    return () => {
      window.removeEventListener(IACRM_CONFIG_EVENT, syncConfigState)
      window.removeEventListener('storage', syncConfigState)
    }
  }, [])

  // Check terms acceptance status on mount
  useEffect(() => {
    if (!user) return

    const checkTermsStatus = async () => {
      try {
        const response = await apiRequest<{
          platform_terms: { accepted: boolean }
          business_terms: { accepted: boolean }
          agent_terms: { accepted: boolean }
        }>('/v1/terms/status')

        const platformAccepted = response.platform_terms?.accepted ?? false
        const businessAccepted = response.business_terms?.accepted ?? false
        const agentAccepted = response.agent_terms?.accepted ?? false

        // For business owners, show platform + business terms if either is not accepted
        if (isBusinessOwner) {
          if (!platformAccepted || !businessAccepted) {
            setTermsModalType('both')
            setTermsModalBusinessId(user?.current_business_id ?? undefined)
            setTermsModalOpen(true)
          }
        } else if (isAgent) {
          // For agents, show platform + agent terms if either is not accepted
          if (!platformAccepted || !agentAccepted) {
            setTermsModalType('agent-both')
            setTermsModalBusinessId(undefined)
            setTermsModalOpen(true)
          }
        } else if (!platformAccepted) {
          // For regular users, only check platform terms
          setTermsModalType('platform')
          setTermsModalBusinessId(undefined)
          setTermsModalOpen(true)
        }
      } catch {
        // Silently fail - terms check is not critical
      }
    }

    checkTermsStatus()
  }, [user, isBusinessOwner])

  const pageContent = (
    <>
      {(isBusinessOwner || isAgent) && <OnboardingHighlight />}
      <header className={`sticky top-0 z-20 w-full ${dashboardSurfaceClass}`}>
        <div className="flex h-14 w-full items-center gap-4 px-4 md:px-6">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => {
                if (sidebarMode === 'drawer') {
                  setMobileSidebarOpen((value) => !value)
                  return
                }
                setSidebarCollapsed((value) => !value)
              }}
              className="-ml-1"
              aria-label={
                sidebarMode === 'drawer'
                  ? mobileSidebarOpen
                    ? t('navigation.sidebar.close')
                    : t('navigation.sidebar.open')
                  : sidebarCollapsed
                    ? t('navigation.sidebar.expand')
                    : t('navigation.sidebar.collapse')
              }
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
            <Separator
              orientation="vertical"
              className="mr-2 hidden data-[orientation=vertical]:h-4 md:block"
            />
            {isDashboardRoute ? (
              <p className="text-sm text-muted-foreground">
                {t('dashboard.hello')}, <span className="font-semibold text-foreground">{displayName}</span>
              </p>
            ) : (
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem className="hidden md:block">
                    <BreadcrumbLink to="/dashboard">{t('navigation.dashboard')}</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="hidden md:block" />
                  {collapsedBreadcrumbTrail.length > 0 || shouldShowEllipsis ? (
                    <>
                      <BreadcrumbItem className="hidden md:block">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-7 cursor-pointer rounded-md"
                              aria-label={t('navigation.breadcrumb.showFullPath')}
                            >
                              <BreadcrumbEllipsis className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="min-w-[12rem]">
                            {collapsedBreadcrumbTrail.length > 0 ? (
                              collapsedBreadcrumbTrail.map((item, index) =>
                                item.to ? (
                                  <DropdownMenuItem
                                    key={`${item.label}-${index}`}
                                    className="cursor-pointer"
                                    onClick={() => navigate(item.to!)}
                                  >
                                    {item.label}
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuLabel key={`${item.label}-${index}`}>
                                    {item.label}
                                  </DropdownMenuLabel>
                                ),
                              )
                            ) : (
                              <DropdownMenuLabel>{activeRoute.title}</DropdownMenuLabel>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                    </>
                  ) : null}
                  {visibleBreadcrumbTrail.map((item, index) => {
                    const isLast = index === visibleBreadcrumbTrail.length - 1
                    return (
                      <span key={`${item.label}-${index}`} className="contents">
                        <BreadcrumbItem>
                          {isLast || !item.to ? (
                            <BreadcrumbPage>{item.label}</BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink to={item.to}>{item.label}</BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                        {!isLast ? <BreadcrumbSeparator className="hidden md:block" /> : null}
                      </span>
                    )
                  })}
                </BreadcrumbList>
              </Breadcrumb>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 md:flex-1 md:justify-end">
            {shouldShowIacrmCta ? (
              <>
                <div className="hidden items-center gap-3 rounded-full border border-amber-200/80 bg-amber-50/90 px-3 py-1.5 text-xs text-amber-950 dark:border-amber-900/80 dark:bg-amber-950/70 dark:text-amber-100 xl:flex">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/80 dark:text-amber-200">
                    <AlertTriangle className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-medium">
                    {t('iacrm.notConfigured')}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 rounded-full bg-amber-500 px-3 text-xs font-semibold text-amber-950 hover:bg-amber-400"
                    onClick={() => navigate('/settings?tab=api')}
                  >
                    {t('iacrm.configureApi')}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-full border-amber-300/80 bg-amber-50/80 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100 dark:hover:bg-amber-900/70 xl:hidden"
                  onClick={() => navigate('/settings?tab=api')}
                >
                  API IACRM
                </Button>
              </>
            ) : null}

            {shouldShowAgentPointsCta ? (
              <>
                <div className="hidden items-center gap-3 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs text-foreground xl:flex">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Gift className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-medium">
                    {t('points.availablePoints', { count: availablePoints })}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                    onClick={() => navigate('/payouts')}
                  >
                    {t('points.usePoints')}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-full border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 xl:hidden"
                  onClick={() => navigate('/payouts')}
                >
                  <Gift className="mr-2 h-4 w-4" />
                  {formatAppNumber(availablePoints)} {t('common.pts')}
                </Button>
              </>
            ) : null}

            <LanguageSwitcher />

            <Button
              onClick={() =>
                setTheme((current) => {
                  const base = current === 'system' ? resolvedTheme : current
                  return base === 'dark' ? 'light' : 'dark'
                })
              }
              variant="ghost"
              size="icon"
              className="relative"
              aria-label={
                resolvedTheme === 'dark'
                  ? t('theme.switchToLight')
                  : t('theme.switchToDark')
              }
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>

            <NotificationBell />

            <Separator
              orientation="vertical"
              className="hidden bg-border/80 data-[orientation=vertical]:h-4 md:block"
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 gap-2 px-2 text-left"
                >
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={user?.avatar_url ?? undefined} alt={user?.display_name ?? 'User'} />
                    <AgentAvatarFallback
                      seed={user ? avatarSeedForUser({ id: user.id }) : 'anonymous'}
                      className="text-xs font-semibold"
                    >
                      {userInitial}
                    </AgentAvatarFallback>
                  </Avatar>
                  <span className="hidden md:block">
                    <span className="block max-w-[140px] truncate text-xs font-semibold text-foreground">
                      {user?.display_name ?? 'User'}
                    </span>
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="truncate text-sm font-medium">{user?.display_name ?? 'User'}</span>
                    <span className="truncate text-xs text-muted-foreground">{user?.email ?? ''}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/settings')}
                  className="cursor-pointer"
                >
                  <User className="h-4 w-4" />
                  <span>{t('settings.personalProfile')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/settings')}
                  className="cursor-pointer"
                >
                  <Settings className="h-4 w-4" />
                  <span>{t('settings.title')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/legal')}
                  className="cursor-pointer"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>{t('legal.pageTitle', 'Legal')}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    {t('theme.title')}
                  </p>
                  <Tabs
                    value={theme}
                    onValueChange={(value) =>
                      setTheme(value as 'light' | 'dark' | 'system')
                    }
                  >
                    <TabsList className="w-full">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TabsTrigger value="light" className="px-2">
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center justify-center">
                                <Sun className="h-4 w-4" />
                              </span>
                            </TooltipTrigger>
                          </TabsTrigger>
                          <TooltipContent side="top">{t('theme.light')}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TabsTrigger value="dark" className="px-2">
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center justify-center">
                                <Moon className="h-4 w-4" />
                              </span>
                            </TooltipTrigger>
                          </TabsTrigger>
                          <TooltipContent side="top">{t('theme.dark')}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TabsTrigger value="system" className="px-2">
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center justify-center">
                                <Monitor className="h-4 w-4" />
                              </span>
                            </TooltipTrigger>
                          </TabsTrigger>
                          <TooltipContent side="top">{t('theme.system')}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TabsList>
                  </Tabs>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout()
                    navigate('/login', { replace: true })
                  }}
                  disabled={logoutPending}
                  variant="destructive"
                  className="cursor-pointer"
                >
                  <LogOut className="h-4 w-4 text-destructive" />
                  <span>{logoutPending ? t('auth.signingOut') : t('auth.signOut')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-4 md:px-6 md:py-6">
        <BreadcrumbDetailTitleContext.Provider value={setBreadcrumbDetailTitle}>
          <BreadcrumbTrailContext.Provider value={setBreadcrumbTrail}>
            <Outlet />
          </BreadcrumbTrailContext.Provider>
        </BreadcrumbDetailTitleContext.Provider>
      </div>

      <CookieConsentBanner />
      <TermsAcceptanceModal
        open={termsModalOpen}
        onOpenChange={setTermsModalOpen}
        type={termsModalType}
        businessId={termsModalBusinessId}
      />
    </>
  )

  if (isMobile) {
    return (
      <main className={`min-h-screen w-screen overflow-x-hidden text-foreground ${dashboardSurfaceClass}`}>
        <div
          className={[
            'flex min-h-screen w-[calc(100vw+16rem)] transition-transform duration-300 will-change-transform',
            mobileSidebarOpen ? 'translate-x-0' : '-translate-x-64',
          ].join(' ')}
        >
          <AppSidebar
            collapsed={false}
            navItems={visibleNavigation}
            iacrmConfigured={iacrmConfigured}
            navBadges={{ '/commissions': availablePointsBadge }}
            mode={sidebarMode}
            isBusinessOwner={isBusinessOwner}
            isAgent={isAgent}
            onNavigate={() => setMobileSidebarOpen(false)}
          />

          <div className={`relative min-h-screen w-screen shrink-0 ${dashboardSurfaceClass}`}>
            {mobileSidebarOpen ? (
              <button
                type="button"
                aria-label="Close sidebar menu"
                className="absolute inset-0 z-30 bg-black/20"
                onClick={() => setMobileSidebarOpen(false)}
              />
            ) : null}
            {pageContent}
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className={`flex min-h-screen w-full text-foreground ${dashboardSurfaceClass}`}>
      <AppSidebar
        collapsed={sidebarCollapsed}
        navItems={visibleNavigation}
        iacrmConfigured={iacrmConfigured}
        navBadges={{ '/commissions': availablePointsBadge }}
        mode={sidebarMode}
        isBusinessOwner={isBusinessOwner}
        isAgent={isAgent}
        onNavigate={() => {}}
      />

      <div className={`relative min-h-screen min-w-0 w-full max-w-full flex-1 ${dashboardSurfaceClass}`}>
        {pageContent}
      </div>
    </main>
  )
}
